// FirstSession.tsx — Task 1B: the warm, founder-led first session. No disclosure
// up front; nothing blocks. Five steps:
//   0 Welcome · 1 Meet your guide · 2 What to expect (pacing) · 3 quote card ·
//   4 account (sign in / create a free account, via the shared AccountSignIn
//   panel) — the final step.
// The breathing-guide taster used to close onboarding but was removed for being
// clunky; the breathing guide is still available from the Today screen.
// Reminders are NOT set here any more — they're surfaced after onboarding as a
// dismissible Craig-fronted tile on the Today screen (see ReminderNudgeTile),
// which opens the You → Reminders settings.
// Additive + flag-gated (config/features FIRST_SESSION_ONBOARDING); the old
// onboarding is retained and used when the flag is off.
import React from 'react';
import { Display, Body, Btn } from '@/components/ui';
import { Icon, Wordmark } from '@/components/icons';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { AccountSignIn } from '@/components/AccountSignIn';
import { useGuide } from '@/flows/guide/useGuide';
import { GUIDE_MESSAGES, clipUrl } from '@/data/guideMessages';
import type { GuideSelection } from '@/services/guidePresence';

const STEP_COUNT = 5; // welcome · guide · what to expect · quote · account

const QUOTE_POOL: GuideSelection['quote'][] = [
  ...(GUIDE_MESSAGES.H1_welcome.quotePool ?? []),
  ...(GUIDE_MESSAGES.H5_mastery.quotePool ?? []),
];

export function FirstSession() {
  const progress = useProgress();
  const auth = useAuth();
  const guide = useGuide();
  const [step, setStep] = React.useState(0);
  // Account step (4) is two pages: a calm "recover gently" choice page offering
  // BOTH paths, then the email form for whichever was picked. Create collects a
  // name (new account); sign in is a returning user (no name).
  const [acctMode, setAcctMode] = React.useState<'create' | 'signin'>('create');
  const [acctPhase, setAcctPhase] = React.useState<'choose' | 'form'>('choose');

  // Guide H1 (welcome) + the quote-card quote — picked once. H1 is recorded once
  // (the welcome screen IS the welcome — shown as a hero image, not a separate tile).
  const [h1] = React.useState<GuideSelection | null>(() => guide.pick('H1_welcome'));
  const [cardQuote] = React.useState(() => QUOTE_POOL[Math.floor(Math.random() * Math.max(1, QUOTE_POOL.length))]);
  React.useEffect(() => {
    if (h1) guide.record(h1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    progress.recordOnboardingStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const next = () => setStep((s) => Math.min(STEP_COUNT - 1, s + 1));
  const finish = () => progress.completeOnboarding();

  // Account step (the final step). With activation on, the user signs in or
  // creates a free account via AccountSignIn; the session appears asynchronously,
  // so completing onboarding once it does. With no backend (dev / ungated)
  // there's nothing to sign into, so the step shows a simple continue.
  React.useEffect(() => {
    if (step === 4 && auth.session) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, auth.session]);

  return (
    // This screen is the sole scroller; overscrollBehavior:contain stops a drag past
    // the end from chaining out to the locked document and swiping the whole app on
    // mobile web (matches AppShell's data-scroll-area).
    <div style={{ position: 'relative', width: '100%', height: '100dvh', maxWidth: 520, margin: '0 auto', background: 'var(--cream)', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
      <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', padding: 'calc(26px + var(--safe-top)) 22px calc(26px + var(--safe-bottom))' }}>
        {/* header: wordmark · progress · skip */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 }}>
          <Wordmark h={20} />
          <div style={{ display: 'flex', gap: 6 }}>
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <span key={i} style={{ width: i === step ? 18 : 6, height: 6, borderRadius: 999, background: i === step ? 'var(--amber-deep)' : i < step ? 'var(--green)' : 'var(--hair-strong)', transition: 'all .3s ease' }} />
            ))}
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {step === 0 && (
            <>
              {/* Landing — brand mark + a one-line subtitle. */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
                <Wordmark h={40} />
              </div>
              <Display parts={{ pre: 'Recovery, ', em: 'gently', post: '.' }} size={27} lh={1.2} style={{ textAlign: 'center' }} />
              <Body size={15} style={{ margin: '16px auto 0', maxWidth: 320, lineHeight: 1.75, textAlign: 'center' }}>
                A clinician-built programme for healing after a controlling or abusive relationship — taken
                slowly, and at your own pace.
              </Body>
              <Btn full size="lg" onClick={next} style={{ marginTop: 28 }}>Get started</Btn>
            </>
          )}

          {step === 1 && (
            <>
              {/* Introduction to the psychologist (the founder guide). */}
              <img
                src={clipUrl('welcome')}
                alt=""
                aria-hidden="true"
                style={{ display: 'block', width: 'auto', maxWidth: '64%', maxHeight: 190, margin: '0 auto 10px', objectFit: 'contain' }}
              />
              <Display parts={{ pre: 'Meet your ', em: 'guide', post: '.' }} size={27} lh={1.2} style={{ textAlign: 'center' }} />
              <Body size={15} style={{ margin: '18px auto 0', maxWidth: 330, lineHeight: 1.8, textAlign: 'center' }}>
                I’m a psychologist, and your guide on the road to recovery. I’ll be here to guide your recovery,
                at your pace, and to point you to the many tools we have.
              </Body>
              <Body size={15} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.8, textAlign: 'center' }}>
                This is a gentle, self-paced programme for healing after a controlling or abusive relationship.
                Let’s begin softly.
              </Body>
              <Btn full size="lg" onClick={next} style={{ marginTop: 26 }}>Continue</Btn>
            </>
          )}

          {step === 2 && (
            <>
              {/* What to expect — pacing reassurance. The most important expectation
                  to set for this audience: there is no finish line and no falling
                  behind. Sets the tone before anything is asked of them. */}
              <Display parts={{ pre: 'Take it at ', em: 'your pace', post: '.' }} size={27} lh={1.2} style={{ textAlign: 'center' }} />
              <Body size={15} style={{ margin: '18px auto 0', maxWidth: 330, lineHeight: 1.8, textAlign: 'center' }}>
                There’s no finish line here, and no falling behind. The programme is made of small steps you
                can take whenever you have the space — a few minutes at a time is plenty.
              </Body>
              <Body size={15} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.8, textAlign: 'center' }}>
                Come and go as you need. Everything waits for you, and nothing is lost when you step away.
              </Body>
              <Btn full size="lg" onClick={next} style={{ marginTop: 26 }}>Continue</Btn>
            </>
          )}

          {step === 3 && (
            <>
              <Eyebrow>In Craig’s words</Eyebrow>
              <img
                src={clipUrl('quote-card')}
                alt=""
                aria-hidden="true"
                style={{ display: 'block', width: 'auto', maxWidth: '48%', maxHeight: 140, margin: '4px auto 16px', objectFit: 'contain' }}
              />
              <blockquote style={{ margin: 0, padding: '0 4px', fontFamily: 'Fraunces, Georgia, serif', fontStyle: 'italic', fontWeight: 400, fontSize: 21, lineHeight: 1.5, color: 'var(--green-deep)', textAlign: 'center' }}>
                “{cardQuote?.text}”
              </blockquote>
              <Btn full size="lg" onClick={next} style={{ marginTop: 26 }}>Continue</Btn>
            </>
          )}

          {step === 4 && (
            <>
              {/* No backend (dev / ungated): nothing to sign into, a simple continue. */}
              {!auth.enabled ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                    <span style={{ width: 64, height: 64, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(200,134,26,0.12)', color: 'var(--amber-deep)' }}>
                      <Icon name="you" size={28} />
                    </span>
                  </div>
                  <Display parts={{ pre: 'Recover, ', em: 'gently', post: '.' }} size={26} lh={1.18} style={{ textAlign: 'center' }} />
                  <Body size={14.5} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
                    A calm, clinician-built space to recover from a difficult relationship, at your own pace and in your own time.
                  </Body>
                  <Btn full size="lg" onClick={finish} leading={<Icon name="you" size={18} />} style={{ marginTop: 24 }}>
                    Continue
                  </Btn>
                </>
              ) : acctPhase === 'choose' ? (
                /* The "recover gently" choice page: create a free account OR sign in. */
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                    <span style={{ width: 64, height: 64, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(200,134,26,0.12)', color: 'var(--amber-deep)' }}>
                      <Icon name="you" size={28} />
                    </span>
                  </div>
                  <Display parts={{ pre: 'Recover, ', em: 'gently', post: '.' }} size={26} lh={1.18} style={{ textAlign: 'center' }} />
                  <Body size={14.5} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
                    A calm, clinician-built space to recover from a difficult relationship, at your own pace and in your own time.
                  </Body>
                  <Body size={13} style={{ margin: '10px auto 0', maxWidth: 330, lineHeight: 1.65, textAlign: 'center', color: 'var(--ink-soft)' }}>
                    A free account opens the first part of the programme, with ten days’ full access to every tool. Membership opens the whole programme whenever you’re ready, and the calming and grounding tools are always free.
                  </Body>
                  <div style={{ marginTop: 26 }}>
                    <Btn full size="lg" onClick={() => { setAcctMode('create'); setAcctPhase('form'); }} leading={<Icon name="leaf" size={18} />}>
                      Create my free account
                    </Btn>
                  </div>
                  <div style={{ textAlign: 'center', marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => { setAcctMode('signin'); setAcctPhase('form'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--green-deep)', padding: '8px 12px' }}
                    >
                      I already have an account
                    </button>
                  </div>
                </>
              ) : (
                /* The email form for whichever path was chosen, with a way back. */
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}>
                    <button
                      type="button"
                      onClick={() => setAcctPhase('choose')}
                      aria-label="Back"
                      style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                      <Icon name="chevLeft" size={18} />
                    </button>
                  </div>
                  <Display
                    parts={acctMode === 'create'
                      ? { pre: 'Create your ', em: 'free account', post: '.' }
                      : { pre: 'Sign in to ', em: 'continue', post: '.' }}
                    size={26}
                    lh={1.18}
                    style={{ textAlign: 'center' }}
                  />
                  <Body size={14.5} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.7, textAlign: 'center' }}>
                    {acctMode === 'create'
                      ? 'Your free 10-day trial unlocks every feature, and the self-compassion course stays yours to keep, free, afterwards.'
                      : 'Welcome back. Enter your email and we’ll send you a sign-in code.'}
                  </Body>
                  <div style={{ marginTop: 18 }}>
                    <AccountSignIn mode={acctMode} />
                  </div>
                </>
              )}
            </>
          )}
        </div>

      </div>
    </div>
  );
}

function Eyebrow({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--green-deep)', marginBottom: 9, ...style }}>
      {children}
    </div>
  );
}

